import { useCallback, useEffect } from 'react';
import { RoiType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { featureFlags } from '../../../feature-flags';
import { trackingIds } from '../../../tracking-ids';
import { useARRoiShallowStore } from '../appointment-reminders/hooks/use-ar-roi-store';
import { useFetchARROIOOverview } from '../appointment-reminders/hooks/use-fetch-ar-roi-overview';
import { useFetchRoiTrendLine } from '../hooks/use-fetch-roi-trend-line';
import { WidgetCardProps } from './types';
import { WidgetCard } from './widget-card';

export const ARWidgetCard = ({ handleUpdateActiveCards, showTrendLine, timePeriod }: WidgetCardProps) => {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.appointmentRemindersRoiDemoData);

  const { setFilters: setARFilters, setDoNotOverrideFiltersOnLoad: setDoNotOverrideARFiltersOnLoad } =
    useARRoiShallowStore('setFilters', 'setDoNotOverrideFiltersOnLoad');

  const filters = {
    startDate: timePeriod.value.startDate,
    endDate: timePeriod.value.endDate,
    locationIds: selectedLocationIds,
  };

  const { aggregated, isLoading } = useFetchARROIOOverview({
    filters,
    fetchNoShowRate: false,
  });

  const appointmentsConfirmed = aggregated?.appointmentsConfirmed?.appointmentsCount || 0;

  const { isLoading: isLoadingTrendLine, trendLine } = useFetchRoiTrendLine({
    additionalRequestPayload: {
      roiType: RoiType.APPOINTMENT_REMINDERS,
    },
    filters,
    isDemoAccount,
    isEnabled: showTrendLine,
  });

  const handleNavigate = useCallback(() => {
    if (!filters) {
      return;
    }

    setARFilters(filters);
    setDoNotOverrideARFiltersOnLoad(true);

    navigate({
      to: '/analytics/roi/appointment-reminders',
    });
  }, [filters]);

  useEffect(() => {
    handleUpdateActiveCards('appointmentReminders', !!appointmentsConfirmed);
  }, [appointmentsConfirmed]);

  if (!appointmentsConfirmed) {
    return null;
  }

  return (
    <WidgetCard
      backgroundConfig={{
        // Colors are not available in the theme
        gradientStart: '#F07006',
        gradientEnd: '#FDB771',
      }}
      iconName='calendar-small'
      isLoading={isLoading || isLoadingTrendLine}
      onClick={handleNavigate}
      subTitle={t('Appointments Confirmed')}
      title={t('Appointment Reminders')}
      trackingId={trackingIds.roi.appointmentRemindersRoiWidget}
      trend={showTrendLine && trendLine}
      value={formatters.value(appointmentsConfirmed)}
    />
  );
};
